import * as React from "react";
import { EditorialProps } from "./typings";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { EditorialModel } from "typings/microserviceModels/editorial-flex-module";
import { UitkIcon } from "uitk-react-icons";
import { decorateEditorialHtml } from "./EditorialUtilities";
import { inject } from "mobx-react";
import { EditorialInfoView } from "./views/EditorialInfoView";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { UitkCard, UitkCardContent } from "uitk-react-cards";
import { EditorialWithLink } from "./views/EditorialWithLink";
import {
  FlexTrackingInfo,
  Action,
  sendDelayedTrackEvent
} from "src/components/utility/analytics/FlexAnalyticsUtils";

/**
 * Gets the component definition we want to render based on the content purpose
 * or lack thereof.
 * @param contentPurpose - The type of editorial that we are receiving
 *                         ({@see EditorialContentPurpose})
 */
function getEditorialItem(
  contentPurpose?: string
): (
  {
    item,
    isMobile
  }: {
    item: EditorialModel;
    isMobile: boolean;
  }
) => JSX.Element {
  if (!contentPurpose) {
    return EditorialNoPurpose;
  }

  const mainPurpose = contentPurpose.split(" ")[0];

  switch (mainPurpose) {
    case EditorialContentPurpose.CONFIDENCE_MESSAGE:
    case EditorialContentPurpose.HIGHLIGHTS:
      return ConfidenceMessageItem;
    case EditorialContentPurpose.LOB_CROSSLINKING:
    case EditorialContentPurpose.EDITORIAL:
    case EditorialContentPurpose.INTRODUCTION:
    case EditorialContentPurpose.EXTENDED_CONTEXT:
    case EditorialContentPurpose.FREE_TEXT:
      return EditorialItem;
    default:
      return EditorialNoPurpose;
  }
}

export enum ModuleViewType {
  CardWithLink = "card-with-link",
  Snippet = "snippet",
  Info = "info"
}

/**
 * Gets the class name for an editorial item based on its content purpose
 * @param contentPurpose - The type of editorial that we are receiving
 *                         ({@see EditorialContentPurpose})
 */
function getEditorialItemClassName(contentPurpose?: string) {
  if (!contentPurpose) {
    return "all-y-padding-three";
  }

  const mainPurpose = contentPurpose.split(" ")[0];

  switch (mainPurpose) {
    case EditorialContentPurpose.HIGHLIGHTS:
    case EditorialContentPurpose.CONFIDENCE_MESSAGE:
      return "confidenceMessage";
    default:
      return "all-y-padding-three";
  }
}

/**
 * Determines if an editorial should show up on the Blossom page. Check out
 * https://trello.com/c/4kuj6Icq/8-editorial-module-content for how these are
 * determined (TL;DR: SEO folk)
 */
function isDisabledContentPurpose(contentPurpose?: string): boolean {
  if (!contentPurpose) {
    return true;
  }

  // Remove numbers after purposes. Ex: Editorial 3 becomes Editorial
  const mainPurpose = contentPurpose.split(" ")[0];

  switch (mainPurpose) {
    case EditorialContentPurpose.HIGHLIGHTS:
    case EditorialContentPurpose.CONFIDENCE_MESSAGE:
    case EditorialContentPurpose.LOB_CROSSLINKING:
    case EditorialContentPurpose.EDITORIAL:
    // Added by request of Cristina Alcocer
    // This has been already checked with UX (Alicia) https://jira.expedia.biz/browse/COR-758
    case EditorialContentPurpose.INTRODUCTION:
    case EditorialContentPurpose.EXTENDED_CONTEXT:
    case EditorialContentPurpose.FREE_TEXT:
      return false;
    default:
      return true;
  }
}

/**
 * An Editorial consists of HTML strings that we parse through with the best of
 * our abilities and display as is on the page. Mainly, we do not control the
 * content of an Editorial, we simply put them in the page.
 */
export const Editorial: React.FC<EditorialProps> = inject("routeStore", "analytics")(props => {
  const flexComponent = props.flexComponent;
  const model = flexComponent.model;
  const keyHelper = new ItemKeyHelper("editorial");
  const contentPurpose = model.contentPurpose;
  const { analytics } = props;

  if (
    !flexComponent ||
    !model ||
    !model.items ||
    model.items.length < 1 ||
    isDisabledContentPurpose(contentPurpose)
  ) {
    return null;
  }

  const classNameAttr = `Editorial all-cell-1-1 uitk-cell ${getEditorialItemClassName(
    contentPurpose
  )}`;

  if (model.view === ModuleViewType.Info) {
    if (model.items.length !== 2) {
      return null;
    }

    return (
      <div
        className={classNameAttr}
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
      >
        <EditorialInfoView items={model.items} props={props} />
      </div>
    );
  }

  if (model.view === ModuleViewType.CardWithLink || model.view === ModuleViewType.Snippet) {
    if (!model.items[0].media) {
      return null;
    }
    const cardClicked = () => {
      const trackingInfo: FlexTrackingInfo = {
        moduleName: flexComponent.name,
        rfrr: flexComponent.model.title.replace(/\s/g, ""),
        action: Action.CLICK
      };

      sendDelayedTrackEvent(trackingInfo, analytics);
    };

    const editorialMargins =
      "all-t-margin-three m-b-margin-three l-b-margin-three xl-b-margin-three";
    const snippetMargins = "all-t-margin-three l-b-margin-three xl-b-margin-three";

    const uitkClassNames =
      model.view === ModuleViewType.CardWithLink ? editorialMargins : snippetMargins;

    return (
      <div
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        className={`uitk-cell uitk-type-center Editorial all-cell-1-1 ${uitkClassNames}`}
        onClick={cardClicked}
        role="button"
      >
        <EditorialWithLink
          flexComponent={props.flexComponent}
          l10n={props.l10n}
          context={props.context}
        />
      </div>
    );
  }

  return (
    <Viewport>
      <ViewSmall>
        <div
          className={classNameAttr}
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
        >
          {model.items
            .filter((item: EditorialModel) => item.text || item.title)
            .map((item: EditorialModel) => {
              const Item = getEditorialItem(contentPurpose);

              return (
                <Item item={item} isMobile={true} key={`${contentPurpose}-${keyHelper.next()}`} />
              );
            })}
        </div>
      </ViewSmall>
      <ViewMedium>
        <div
          className={classNameAttr}
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
        >
          {model.items
            .filter((item: EditorialModel) => item.text || item.title)
            .map((item: EditorialModel) => {
              const Item = getEditorialItem(contentPurpose);

              return (
                <Item item={item} isMobile={false} key={`${contentPurpose}-${keyHelper.next()}`} />
              );
            })}
        </div>
      </ViewMedium>
    </Viewport>
  );
});

/**
 * Editorial Item which will wrap text in a split column for desktop pages as
 * well as a card or will give some styling for mobile pages. This may replace
 * {@see EditorialNoPurpose} eventually.
 */
function EditorialItem({ item, isMobile }: { item: EditorialModel; isMobile: boolean }) {
  const textCSSClass = "all-t-padding-two uitk-type-list-300 editorialTextColumn";

  if (isMobile) {
    return (
      <div className="uitk-type-paragraph-300" data-fm-content={item.id}>
        {item.title && <h2 className="uitk-type-heading-600">{item.title}</h2>}
        {item.text && (
          <div
            className="all-t-padding-two uitk-type-list-300"
            dangerouslySetInnerHTML={decorateEditorialHtml(item.text)}
          />
        )}
      </div>
    );
  }

  return (
    <UitkCard>
      <UitkCardContent>
        <div className="uitk-card-text-segment uitk-grid">
          <div className="uitk-cell s-cell-1-1 m-cell-1-1 l-cell-1-1 xl-cell-1-1">
            <div className="uitk-type-paragraph-300" data-fm-content={item.id}>
              {item.title && <h2 className="uitk-type-heading-600">{item.title}</h2>}
              {item.text && (
                <div
                  className={textCSSClass}
                  dangerouslySetInnerHTML={decorateEditorialHtml(item.text)}
                />
              )}
            </div>
          </div>
        </div>
      </UitkCardContent>
    </UitkCard>
  );
}

/**
 * Editorial item for a {@see EditorialContentPurpose.CONFIDENCE_MESSAGE}
 */
function ConfidenceMessageItem({ item }: { item: EditorialModel }) {
  return (
    <div
      className="confidenceMessageItem uitk-type-paragraph-300"
      data-testid="confidenceMsg"
      data-fm-content={item.id}
    >
      <UitkIcon className="confidenceMessageIcon" name="done" data-testid="confidenceIcon" />
      <span dangerouslySetInnerHTML={decorateEditorialHtml(item.text)} />
    </div>
  );
}

/**
 * Extremely bare editorial for when we have not yet handled how the content
 * purpose or lack thereof should be handled. This has only the bare minimum of
 * styles in order to not mess with any of the given HTML which is unknown in
 * structure.
 */
function EditorialNoPurpose({ item }: { item: EditorialModel }) {
  return (
    <div data-fm-content={item.id}>
      {item.title && <h2 className="uitk-type-heading-600">{item.title}</h2>}
      {item.text && <div dangerouslySetInnerHTML={decorateEditorialHtml(item.text)} />}
    </div>
  );
}

export default Editorial;

// Flex manager sets an optional string contentPurpose for each Editorial.
// IMPORTANT: This was manually added from flex manager
// https://flexmanager.prod.expedia.com/#/flexTemplate/list?containsModule=editorial&pageType=TRAVEL_GUIDE_HOTELS&attributeName=contentPurpose&offset=0&max=20&order=desc&userId=30714061
export enum EditorialContentPurpose {
  EDITORIAL = "Editorial",
  LOB_CROSSLINKING = "LobCrosslinking",
  HIGHLIGHTS = "Highlights",
  PROMOTIONAL = "Promotional",
  IMAGE_SCAPE = "ImageScape",
  THIRD_PARTY = "3rdParty",
  CARDS = "Cards",
  CONFIDENCE_MESSAGE = "ConfidenceMessage",
  CONFIDENCE_MESSAGE_2 = "ConfidenceMessage 2",
  CONFIDENCE_MESSAGE_3 = "ConfidenceMessage 3",
  CONFIDENCE_MESSAGE_4 = "ConfidenceMessage 4",
  CONFIDENCE_MESSAGE_5 = "ConfidenceMessage 5",
  CONFIDENCE_MESSAGE_6 = "ConfidenceMessage 6",
  COUPONS = "Coupons",
  COUPONS_1 = "Coupons 1",
  COUPONS_2 = "Coupons 2",
  COUPONS_3 = "Coupons 3",
  CROSSLINKING_1 = "Crosslinking 1",
  CROSSLINKING_2 = "Crosslinking 2",
  CROSSLINKING_3 = "Crosslinking 3",
  CROSSLINKING_4 = "Crosslinking 4",
  CROSSLINKING_5 = "Crosslinking 5",
  CROSSLINKING_6 = "Crosslinking 6",
  DEALS = "Deals",
  DEALS1 = "Deals 1",
  DEALS10 = "Deals 10",
  DEALS_11 = "Deals 11",
  DEALS_12 = "Deals 12",
  DEALS_13 = "Deals 13",
  DEALS_14 = "Deals 14",
  DEALS_15 = "Deals 15",
  DEALS_2 = "Deals 2",
  DEALS_3 = "Deals 3",
  DEALS_4 = "Deals 4",
  DEALS_5 = "Deals 5",
  DEALS_6 = "Deals 6",
  DEALS_7 = "Deals 7",
  DEALS_8 = "Deals 8",
  DEALS_9 = "Deals 9",
  DESTINATION_OVERVIEW = "Destination Overview",
  EXTENDED_CONTEXT = "ExtendedContent",
  FLIGHT_STATISTICS = "Flight Statistics",
  FREE_TEXT = "FreeText",
  GLOBAL_CAR_LINKS = "GlobalCarLinks",
  INTRODUCTION = "Introduction",
  INVENTORY = "Inventory",
  LEGAL = "Legal",
  POCKET_GUIDE = "PocketGuide",
  PRACTICAL_ADVICE = "Practical Advice",
  SECTION_HEADER = "SectionHeader",
  SECTION_HEADER_1 = "SectionHeader 1",
  SECTION_HEADER_2 = "SectionHeader 2",
  SECTION_HEADER_3 = "SectionHeader 3",
  SECTION_HEADER_4 = "SectionHeader 4",
  SEO_LINKS = "SEOLinks",
  SEO_TITLE = "SEOTitle",
  SNAPSHOT = "Snapshot",
  TROVER_IMAGES = "TroverImages",
  WHEN_TO_GO = "When to Go",
  WIZARD_FOOTER = "WizardFooter"
}
